import { formatCodeableConcept } from '@medplum/core';
import type { CodeableConcept } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { getMedicationDosageText } from '../../utils/medicationFormatting';
import { getMedicationDosage, getMedicationTitleParts, type MedicationListRecord } from '@liza/fhir-utils';
import { type PromotionStatus, RecordListItem } from './RecordListItem';

export type MedicationsListProps = {
  medications: MedicationListRecord[];
  /** How many raw records collapsed into each displayed row, keyed by the displayed record's id.
   * A row whose count is absent or 1 renders no badge — only a genuine grouping is worth annotating.
   * See `groupMedicationRecords`. */
  groupSizeById?: Map<string, number>;
  onSelectMedication?: (id: string, resourceType: 'MedicationRequest' | 'MedicationStatement') => void;
  selectedMedicationId?: string;
  selectedMedicationResourceType?: 'MedicationRequest' | 'MedicationStatement';
  onPromotionClick?: (id: string) => void;
  /** When set, only matching rows show a promotion action (defaults to all rows when omitted). */
  promotionEligible?: (medication: MedicationListRecord) => boolean;
  getPromotionStatus?: (id: string) => PromotionStatus;
};

/** The row's name, preferring the same split the care plan renders so a medication reads the same on
 * both. Falls back to the concept text (or a `medicationReference` display, which carries no
 * structured names) when there is nothing composable. */
const getMedicationName = (medication: MedicationListRecord): string | undefined =>
  getMedicationTitleParts(medication)?.name ??
  (medication.medicationCodeableConcept
    ? formatCodeableConcept(medication.medicationCodeableConcept)
    : medication.medicationReference
      ? medication.medicationReference.display
      : undefined);

const getDosageText = (medication: MedicationListRecord): string | undefined =>
  getMedicationDosageText(getMedicationDosage(medication));

const firstCategoryConcept = (
  category: CodeableConcept | CodeableConcept[] | undefined,
): CodeableConcept | undefined => {
  if (!category) {
    return undefined;
  }
  return Array.isArray(category) ? category[0] : category;
};

/** The row's secondary lines: the medication's strength and form, then its dose and category. Two
 * lines rather than one so the list reads like the care plan, which shows the strength/form directly
 * beneath the name. Spans, not blocks — `RecordListItem` places its description inside a span. */
const medicationRowDescription = (strengthForm: string | undefined, detail: string): React.ReactNode => (
  <span className="flex flex-col">
    {strengthForm && <span data-testid="medication-subtitle">{strengthForm}</span>}
    <span>{detail}</span>
  </span>
);

export const MedicationsList = ({
  medications,
  groupSizeById,
  onSelectMedication,
  selectedMedicationId,
  selectedMedicationResourceType,
  onPromotionClick,
  promotionEligible,
  getPromotionStatus,
}: MedicationsListProps): React.ReactElement => {
  const { t } = useTranslation('records');

  return (
    <div className="mx-auto w-full max-w-4xl">
      <div className="space-y-2">
        {medications.length > 0 ? (
          medications.map((medication) => {
            const name = getMedicationName(medication);
            const strengthForm = getMedicationTitleParts(medication)?.subtitle;
            const dosageText = getDosageText(medication);
            const categoryConcept = firstCategoryConcept(medication.category);
            const category = categoryConcept ? formatCodeableConcept(categoryConcept) : undefined;
            const detail = [dosageText ?? t('unknown'), category].filter(Boolean).join(' · ');
            const rowKey = `${medication.resourceType}-${medication.id}`;
            const groupSize = groupSizeById?.get(medication.id);
            const showPromotion =
              Boolean(onPromotionClick) && (promotionEligible ? promotionEligible(medication) : true);

            return (
              <RecordListItem
                key={rowKey}
                testId="medication-record-row"
                title={name || t('unknownMedication')}
                titlePrefix={groupSize && groupSize > 1 ? `${groupSize}×` : undefined}
                description={medicationRowDescription(strengthForm, detail)}
                onClick={
                  onSelectMedication
                    ? (): void => onSelectMedication(medication.id, medication.resourceType)
                    : undefined
                }
                selected={
                  selectedMedicationId === medication.id && selectedMedicationResourceType === medication.resourceType
                }
                onPromotionClick={showPromotion ? (): void => onPromotionClick?.(medication.id) : undefined}
                promotionStatus={getPromotionStatus?.(medication.id)}
              />
            );
          })
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('noMedicationsFound')}</p>
        )}
      </div>
    </div>
  );
};
